import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  MapPin,
  Loader2,
  Navigation,
  AlertCircle,
  X,
  Building2,
  Landmark as LandmarkIcon,
  Route as RouteIcon,
  Home,
  Trees,
  Briefcase,
  Store,
  Plane,
} from "lucide-react";
import { Input } from "@/components/ui/input";

/**
 * NominatimAutocomplete (improved)
 * ---------------------------------------------------------------------------
 * Reusable location search powered by OpenStreetMap Nominatim. No API key,
 * no billing. Tuned for India-focused use (apartments, landmarks, tech parks,
 * hotels, societies, business names) by:
 *   • format=jsonv2 + addressdetails=1 + limit=10  (richer, more results)
 *   • Searching for amenities / shops / buildings / POIs in addition to addresses
 *   • Showing City, State, Country + a category badge under each result
 *   • Prioritising exact-prefix matches before sorting by Nominatim importance
 *   • Soft "tip" row when results < 3
 *   • Optional debug logging via prop (or by setting window.__NOMINATIM_DEBUG)
 *
 * Props:
 *   value, onChange, onSelect, placeholder, testid, showCurrentLocation,
 *   iconColor, countryCodes ("in" by default), minChars (default 2),
 *   debug (default false)
 *
 * Selected location passed to onSelect:
 *   { name, latitude, longitude }
 * ---------------------------------------------------------------------------
 */

// Map Nominatim category/type → human badge + icon
const categoryBadge = (item) => {
  // jsonv2 returns `category` (e.g. "place", "highway", "building",
  // "amenity", "tourism", "shop", "office") and `type` for fine-grain
  const cat = (item.category || item.class || "").toLowerCase();
  const type = (item.type || "").toLowerCase();

  if (cat === "place" && ["city", "town", "municipality"].includes(type))
    return { label: "City", icon: Building2, color: "bg-blue-50 text-blue-700 border-blue-200" };
  if (cat === "place" && ["village", "hamlet", "suburb", "neighbourhood"].includes(type))
    return { label: "Village", icon: Trees, color: "bg-emerald-50 text-emerald-700 border-emerald-200" };
  if (cat === "highway" || type === "road" || type === "street")
    return { label: "Road", icon: RouteIcon, color: "bg-slate-100 text-slate-700 border-slate-200" };
  if (cat === "building" || cat === "place" && type === "house")
    return { label: "Building", icon: Home, color: "bg-amber-50 text-amber-700 border-amber-200" };
  if (cat === "office")
    return { label: "Office", icon: Briefcase, color: "bg-indigo-50 text-indigo-700 border-indigo-200" };
  if (cat === "shop")
    return { label: "Business", icon: Store, color: "bg-pink-50 text-pink-700 border-pink-200" };
  if (cat === "aeroway" || type === "aerodrome" || type === "airport")
    return { label: "Airport", icon: Plane, color: "bg-sky-50 text-sky-700 border-sky-200" };
  if (cat === "tourism" || cat === "historic" || cat === "amenity" || cat === "leisure")
    return { label: "Landmark", icon: LandmarkIcon, color: "bg-purple-50 text-purple-700 border-purple-200" };
  // default
  return { label: type ? type.replace(/_/g, " ") : "Place", icon: MapPin, color: "bg-slate-100 text-slate-600 border-slate-200" };
};

// Build a short subline: "City, State, Country"
const buildSubline = (item) => {
  const a = item.address || {};
  const city = a.city || a.town || a.village || a.suburb || a.county || "";
  const state = a.state || a.state_district || a.region || "";
  const country = a.country || "";
  return [city, state, country].filter(Boolean).join(", ");
};

// Get the headline name — prefer the named entity, fall back to first chunk of display_name
const buildHeadline = (item) => {
  const a = item.address || {};
  const named =
    a.attraction ||
    a.tourism ||
    a.amenity ||
    a.building ||
    a.shop ||
    a.office ||
    a.aeroway ||
    a.house_name ||
    a.road ||
    a.suburb ||
    a.neighbourhood ||
    a.city ||
    a.town ||
    a.village ||
    null;
  if (named) return named;
  return (item.display_name || "").split(",")[0];
};

const NominatimAutocomplete = ({
  value,
  onChange,
  onSelect,
  placeholder = "Search location",
  testid = "nominatim-input",
  showCurrentLocation = false,
  iconColor = "#0B1120",
  countryCodes = "in",
  minChars = 2,
  debug = false,
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [noResults, setNoResults] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const [geoLoading, setGeoLoading] = useState(false);
  const [geoError, setGeoError] = useState("");

  const containerRef = useRef(null);
  const debounceRef = useRef(null);
  const abortRef = useRef(null);

  const debugOn = debug || (typeof window !== "undefined" && window.__NOMINATIM_DEBUG);

  // Close on outside click
  useEffect(() => {
    const onDocClick = (e) => {
      if (!containerRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  // Cleanup pending fetch on unmount
  useEffect(() => () => abortRef.current?.abort(), []);

  const fetchSuggestions = useCallback(
    async (query) => {
      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      setLoading(true);
      setError("");
      setNoResults(false);

      // jsonv2 + addressdetails + limit=10
      const params = new URLSearchParams({
        q: query,
        format: "jsonv2",
        addressdetails: "1",
        limit: "10",
        "accept-language": "en",
      });
      if (countryCodes) params.append("countrycodes", countryCodes);

      const url = `https://nominatim.openstreetmap.org/search?${params.toString()}`;
      if (debugOn) {
        // eslint-disable-next-line no-console
        console.log("[Nominatim] →", url);
      }

      try {
        const res = await fetch(url, {
          signal: controller.signal,
          headers: { Accept: "application/json" },
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();

        if (debugOn) {
          // eslint-disable-next-line no-console
          console.log("[Nominatim] ←", data);
        }

        const list = Array.isArray(data) ? data : [];
        // Prioritise: exact prefix > contains > importance score
        const q = query.toLowerCase().trim();
        const ranked = [...list].sort((a, b) => {
          const an = (a.display_name || "").toLowerCase();
          const bn = (b.display_name || "").toLowerCase();
          const aPrefix = an.startsWith(q) ? 0 : an.includes(q) ? 1 : 2;
          const bPrefix = bn.startsWith(q) ? 0 : bn.includes(q) ? 1 : 2;
          if (aPrefix !== bPrefix) return aPrefix - bPrefix;
          return (b.importance || 0) - (a.importance || 0);
        });

        setSuggestions(ranked);
        setNoResults(ranked.length === 0);
        setLoading(false);
      } catch (err) {
        if (err.name === "AbortError") return;
        if (debugOn) {
          // eslint-disable-next-line no-console
          console.error("[Nominatim] error", err);
        }
        setLoading(false);
        setError("Could not load suggestions. Please try again.");
        setSuggestions([]);
      }
    },
    [countryCodes, debugOn]
  );

  const handleChange = (e) => {
    const v = e.target.value;
    onChange?.(v);
    setHighlight(-1);

    if (!v.trim()) {
      setOpen(false);
      setSuggestions([]);
      setNoResults(false);
      setError("");
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (abortRef.current) abortRef.current.abort();
      return;
    }

    if (v.trim().length < minChars) {
      setSuggestions([]);
      setNoResults(false);
      setOpen(false);
      return;
    }

    setOpen(true);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(v.trim()), 300);
  };

  const handleSelect = (item) => {
    const name = item.display_name;
    onChange?.(name);
    onSelect?.({
      name,
      latitude: item.lat,
      longitude: item.lon,
    });
    setOpen(false);
    setSuggestions([]);
    setNoResults(false);
  };

  const clearInput = () => {
    onChange?.("");
    setSuggestions([]);
    setNoResults(false);
    setOpen(false);
    setError("");
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (abortRef.current) abortRef.current.abort();
  };

  const handleKeyDown = (e) => {
    if (!open) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(suggestions.length - 1, h + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(0, h - 1));
    } else if (e.key === "Enter" && highlight >= 0 && suggestions[highlight]) {
      e.preventDefault();
      handleSelect(suggestions[highlight]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  const detectCurrentLocation = () => {
    if (!navigator.geolocation) {
      setGeoError("Geolocation not supported on this device");
      return;
    }
    setGeoError("");
    setGeoLoading(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude: lat, longitude: lon } = pos.coords;
        try {
          const res = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lon}&addressdetails=1`,
            { headers: { Accept: "application/json" } }
          );
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const data = await res.json();
          const name = data?.display_name || `${lat}, ${lon}`;
          onChange?.(name);
          onSelect?.({ name, latitude: String(lat), longitude: String(lon) });
        } catch (err) {
          setGeoError("Could not resolve your address");
        } finally {
          setGeoLoading(false);
        }
      },
      (err) => {
        setGeoLoading(false);
        if (err.code === 1) setGeoError("Permission denied. Allow location access.");
        else setGeoError("Could not get your location. Please try again.");
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    );
  };

  const showDropdown = open && value && value.trim().length >= minChars;

  return (
    <div ref={containerRef} className="relative">
      {/* Input */}
      <div className="relative">
        <MapPin
          className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4"
          style={{ color: iconColor }}
        />
        <Input
          data-testid={testid}
          type="text"
          value={value}
          onChange={handleChange}
          onFocus={() => value && suggestions.length > 0 && setOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          autoComplete="off"
          className="h-12 pl-9 pr-9 rounded-xl border-slate-200 bg-white"
        />
        {value && (
          <button
            type="button"
            onClick={clearInput}
            aria-label="Clear"
            data-testid={`${testid}-clear`}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 h-6 w-6 rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-100 flex items-center justify-center"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      {/* "Use My Current Location" */}
      {showCurrentLocation && (
        <button
          type="button"
          onClick={detectCurrentLocation}
          data-testid={`${testid}-use-current-location`}
          className="mt-1.5 inline-flex items-center gap-1.5 text-xs font-semibold text-[#0B1120] hover:text-amber-700 transition-colors"
        >
          {geoLoading ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <Navigation className="h-3 w-3 text-[#F5C518]" />
          )}
          Use My Current Location
        </button>
      )}

      {geoError && (
        <div className="mt-1 flex items-center gap-1 text-[11px] text-red-600">
          <AlertCircle className="h-3 w-3" /> {geoError}
        </div>
      )}

      {/* Dropdown */}
      {showDropdown && (
        <div
          data-testid={`${testid}-suggestions`}
          className="absolute z-30 mt-1 w-full max-h-[22rem] overflow-auto rounded-xl border border-slate-200 bg-white shadow-xl"
        >
          {loading && (
            <div className="px-4 py-3 flex items-center gap-2 text-sm text-slate-500">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Searching…
            </div>
          )}

          {!loading && error && (
            <div className="px-4 py-3 flex items-center gap-2 text-sm text-red-600">
              <AlertCircle className="h-3.5 w-3.5" /> {error}
            </div>
          )}

          {!loading && !error && noResults && (
            <div className="px-4 py-3 text-sm text-slate-500">No locations found</div>
          )}

          {!loading && !error && suggestions.length > 0 && (
            <>
              <ul role="listbox">
                {suggestions.map((s, i) => {
                  const badge = categoryBadge(s);
                  const Icon = badge.icon;
                  const headline = buildHeadline(s);
                  const subline = buildSubline(s);
                  return (
                    <li
                      key={`${s.place_id}-${i}`}
                      role="option"
                      aria-selected={highlight === i}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => handleSelect(s)}
                      data-testid={`${testid}-suggestion-${i}`}
                      className={`px-3 py-2.5 cursor-pointer flex items-start gap-2.5 text-sm transition-colors border-b border-slate-50 last:border-b-0 ${
                        highlight === i
                          ? "bg-amber-50"
                          : "hover:bg-slate-50 active:bg-amber-100"
                      }`}
                    >
                      <Icon className="h-4 w-4 mt-0.5 text-slate-400 shrink-0" />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-[#0B1120] truncate">
                            {headline}
                          </span>
                          <span
                            className={`text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full border ${badge.color}`}
                          >
                            {badge.label}
                          </span>
                        </div>
                        {subline && (
                          <div className="text-xs text-slate-500 truncate mt-0.5">
                            {subline}
                          </div>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
              {suggestions.length < 3 && (
                <div
                  data-testid={`${testid}-tip`}
                  className="px-3 py-2 text-[11px] text-slate-500 bg-slate-50 border-t border-slate-100"
                >
                  Can't find your location? Try entering city, area, landmark, or pincode.
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default NominatimAutocomplete;
